define([
    'edge/platform/base/BaseCtrl',
    'underscore',
    'angular',
], (BaseCtrl, _, angular) => {

    const fn = function ($scope, $q, $filter, ModalService, LoadSaveService, ViewModelService, CustomQuoteService, MFPService, MFPCoverageService, DefaultErrorHandlerService, DTO_CLASSES, submissionViewModel, StateUtils, ContextService, BaslerCommonService, LoggingService, BaslerLoggingConstants, RecurrentUserService) { // NOSONAR

        const ctrl = Object.create(BaseCtrl);

        /*
         * Interface definitions (i.e. used by html)
         */
        ctrl.init = initializeData; // called on page load
        ctrl.getCoverageFromOffering = getCoverageViewFromOffering;
        ctrl.compareOfferings = compareOfferings;
        ctrl.handleSelectedOfferingChangedEvent = handleSelectedOfferingChangedEvent;
        ctrl.onChangeCoverageFromModal = onChangeCoverageFromModal;
        ctrl.onToggleCoverageFromModal = onToggleCoverageFromModal;
        ctrl.resetCustomization = resetCustomization;
        ctrl.addRemoveClass = addRemoveClass;
        ctrl.isTouchDevice = isTouchDevice;
        ctrl.goToPrevious = goToPrevious;
        ctrl.selectAndContinue = function (selectedQuoteView) {
            setSelectedQuoteOnModel(selectedQuoteView.value);
            return continueToNextPage();
        };
        ctrl.getTopTranslationKey = () => (ContextService.isPartnerLeasingProduct() ? 'quoteandbind.views.mfp-confirmation.offer.optimized.offers-Leasing' : 'quoteandbind.views.mfp-confirmation.offer.optimized.offers');

        function initializeData() {
            if (!$scope.quoteandbind) {
                const submissionVm = submissionViewModel();
                const submission = submissionVm.value;

                $scope.quoteandbind = {
                    submission: submission,
                    submissionView: submissionVm,
                };
            }

            $scope.offerings = createOfferingViews($scope.quoteandbind.submission.quoteData);
            $scope.coverageStructure = getCoverageStructure($scope.offerings);
            if (!$scope.selectedOffering) {
                $scope.selectedOffering = findSelectedOffering($scope.offerings);
            }

            if ($scope.quoteandbind.submission.loadedQuoteAdjusted) {
                const title = $filter('translate')('quoteandbind.mfp.directives.templates.mfp-risk-relevant-data.PeriodStartDateInPast.Title');
                const message = $filter('translate')(
                    'quoteandbind.mfp.directives.templates.mfp-risk-relevant-data.PeriodStartDateInPast.Corrected',
                    { correctedDate: BaslerCommonService.formatLocalDate($scope.quoteandbind.submission.draftData.periodStartDate) }
                );
                ModalService.showInfo(title, message);
                $scope.quoteandbind.submission.loadedQuoteAdjusted = false;
            }

            RecurrentUserService.setDataToLocalStorage($scope.quoteandbind.submission, ContextService.isMotorcycleMode());
        }

        /*
         * Implementation details
         */
        function createOfferingViews(quoteData) {
            return _.chain(quoteData.offeredQuotes)
                .filter(quote => quote.isCustom === false)
                .map(quote => ViewModelService.create(quote, 'pc', DTO_CLASSES.QUOTE_DTO))
                .value();
        }

        function findSelectedOffering(offeringViews) {
            return _.find(offeringViews, offeringView => offeringView.isSelected.value === true);
        }

        function getCoverageStructure(offeringViews) {
            const offeringModels = _.pluck(offeringViews, 'value');
            return MFPCoverageService.getCoverageStructureFromOfferings(offeringModels);
        }

        function onToggleCoverageFromModal(offeringView, oldCoverageView, tableGroup, newCoverageFromModal, setModalCoverage, setModalLoading, closeModal) {
            setModalLoading(true);
            const coverageCode = newCoverageFromModal.patternCode;
            MFPService.toggleCoverage($scope.quoteandbind.submission, offeringView.value, coverageCode, tableGroup)
                .then((newOffering) => {
                    // update offering model, rebuild table
                    MFPService.updateOffering(offeringView.value, newOffering);
                    initializeData();
                    // update modal content -> there might be new covterms available etc.
                    const newCoverageFromResponse = getCoverage(offeringView.value, newCoverageFromModal.patternCode, tableGroup);
                    setModalCoverage(newCoverageFromResponse);
                })
                .catch((error) => {
                    // close modal and go to error page
                    closeModal();
                    // replace coverage in modal with old one / reset
                    setModalCoverage(oldCoverageView.value);
                    DefaultErrorHandlerService.getErrorHandlerForError(error)();
                })
                .finally(() => {
                    setModalLoading(false);
                });
        }

        function addRemoveClass(coverageView) {
            return coverageView.selected.value ? 'remove-coverage' : 'add-coverage';
        }

        function getCoverageViewFromOffering(offeringView, coverageCode, group) {
            const coverage = getCoverage(offeringView.value, coverageCode, group);
            return ViewModelService.create(coverage, 'pc', DTO_CLASSES.COVERAGE_DTO);
        }

        function compareOfferings(offering1, offering2) {
            return offering1.branchCode.value === offering2.branchCode.value;
        }

        function handleSelectedOfferingChangedEvent(selectedOffering) {
            $scope.$broadcast(
                'selectedOfferingChanged',
                {docId: selectedOffering.docID.value},
            );
            $scope.selectedOffering = selectedOffering;
        }

        function resetCustomization(offerView) {
            MFPService.reset($scope.quoteandbind.submission, offerView.value)
                .then(initializeData);
        }

        /**
         * This reacts to changes to coverage terms from the BaslerEditCoverage modal. When a covterm is changed,
         * this method will:
         * 1. Create a copy of the offering where the selected coverage is replaced with the edited coverage from the modal
         * 2. Mark the modal as loading (i.e. it will display a loading indicator)
         * 3. Call the CustomOfferService with that offering copy
         * 4a. If the call is successful:
         *     - update the current offering with data from the response
         *     - reload the table
         *     - set the coverage that is displayed in the modal with the coverage from the response
         * 4b. If the call throws an error:
         *     - set the coverage that is displayed in the modal to the already existing one from before the call
         *     - call a generic error handler (which will create a popup with an error message)
         * 5. Unmark the modal for loading (no loading indicator anymore)
         * @param offeringView  the offering view for the current offering. This comes from BaslerSideBySideView and contains "old data" from before the change.
         * @param oldCoverageView   the coverage view for the currently edited offering, but in the state from before editing (the editing modal operates on a copy). This comes from BaslerSideBySideView.
         * @param tableGroup    the tableGroup of the current offering in BaslerSideBySideView. This comes from baslerSideBySideView.
         * @param newCoverageFromModal  The edited version from the modal that contains edited data. This comes from the BaslerEditCoverage modal.
         * @param setModalCoverage   This method allows to set the currently displayed coverage model on the modal.
         * @param setModalLoading    This method allows to (un-)mark the modal as loading, i.e. make it display a loading indicator or not.
         * @param closeModal         This is a callback to close the popup. Used in error cases. This is needed not to show 2 popups: EditCoverage and Error
         */
        function onChangeCoverageFromModal(offeringView, oldCoverageView, tableGroup, newCoverageFromModal, setModalCoverage, setModalLoading, closeModal) {
            setModalLoading(true);
            const offeringCopy = createOfferingCopyWithUpdatedCoverage(offeringView.value, newCoverageFromModal, tableGroup);
            saveOffering(offeringCopy)
                .then((response) => {
                    // update offering model, rebuild table
                    MFPService.updateOffering(offeringView.value, response);
                    initializeData();
                    // update modal content -> there might be new covterms available etc.
                    const newCoverageFromResponse = getCoverage(offeringView.value, newCoverageFromModal.patternCode, tableGroup);
                    setModalCoverage(newCoverageFromResponse);
                })
                .catch((error) => {
                    // close modal and go to error page
                    closeModal();

                    // replace coverage in modal with old one / reset
                    setModalCoverage(oldCoverageView.value);
                    DefaultErrorHandlerService.getErrorHandlerForError(error)();
                })
                .finally(() => {
                    setModalLoading(false);
                });
        }

        function isTouchDevice() {
            return isTouchDevice = 'ontouchstart' in document.documentElement;
        }

        function saveOffering(offering) {
            const sessionUUID = $scope.quoteandbind.submission.sessionUUID;
            const quoteId = $scope.quoteandbind.submission.quoteID;
            return CustomQuoteService.updateCustomQuote('quoteandbind', offering, sessionUUID, quoteId);
        }

        function createOfferingCopyWithUpdatedCoverage(offering, coverage, group) {
            const offeringCopy = angular.copy(offering);
            const offeringCopyCoverage = getCoverage(offeringCopy, coverage.patternCode, group);
            offeringCopyCoverage.terms = coverage.terms;
            return offeringCopy;
        }

        function getCoverage(offering, patternCode, group) {
            return MFPCoverageService.getCoverageFromMfpLineByCodeAndGroup(offering.lobs.mFPLine, patternCode, group);
        }

        function setSelectedQuoteOnModel(quote) {
            // Stores the chosen quote in the scope, waiting for the next screen to gather any missing
            // information before binding
            $scope.quoteandbind.chosenQuote = quote;
            MFPService.setSelectedQuote($scope.quoteandbind.submission, quote);
        }

        function continueToNextPage() {
            LoggingService.logInfo(BaslerLoggingConstants.CHANGEPAGE);
            /*
            * The set of the dataProcessingConfirmed to true is needed because otherwise the edge api breaks.
            * But there is a $watch inside BaslerEmailOffer.js which updates the ContextService value if the dataProcessingConfirmed property is changing.
            * This is the reason why the value first is saved into savedConfirmation and afterwards updated inside a timeout function (to ensure it is really happening at the end).
            * */
            const savedConfirmation = ContextService.getDataProcessingConfirmed();
            $scope.quoteandbind.submission.draftData.dataProcessingConfirmed = true;
            setTimeout(() => {
                ContextService.setDataProcessingConfirmed(savedConfirmation);
            })
            return StateUtils.activeDataForState(true).flowModel.next($scope.quoteandbind.submissionView);
        }

        function goToPrevious() {
            StateUtils.activeDataForState(true).flowModel.previous($scope.quoteandbind.submissionView);
        }

        return ctrl.create($scope);
    };
    fn.$inject = ['$scope', '$q', '$filter', 'ModalService', 'qnb.LoadSaveService', 'ViewModelService', 'qnb.CustomQuoteService', 'MFPService', 'MFPCoverageService', 'DefaultErrorHandlerService', 'DTO_CLASSES', 'submissionViewModel', 'StateUtils', 'ContextService', 'BaslerCommonService', 'LoggingService', 'BaslerLoggingConstants', 'RecurrentUserService'];

    return fn;
});



// WEBPACK FOOTER //
// ../common/js/src/edge/quoteandbind/mfp/controllers/MFPQuoteDetailsCtrl.js